export const ROLE_COLORS = {
  customer: "text-amber-600",
  sa: "text-blue-600",
  ccm: "text-purple-600",
  gm: "text-emerald-600",
  psf: "text-rose-600",
  wm: "text-indigo-600",
  receptionist: "text-teal-600",
  system: "text-slate-500",
  unknown: "text-slate-400",
};

export function resolveSenderName(msg, group, stakeholders, roleShort) {
  const role = msg.sender_role;
  if (role === "customer") return group?.["Customer Name"] || group?.customer_name || "Customer";
  if (role === "system") return "Automated";
  if (role === "sa") {
    return (
      group?.["Service Advisor Name"] ||
      group?.service_advisor_name ||
      (roleShort ? roleShort("sa") : "Service Advisor")
    );
  }
  const digits = (msg.sender_number || "").replace(/\D/g, "").slice(-10);
  if (digits && stakeholders?.length) {
    const match = stakeholders.find(
      (s) => (s.number || "").replace(/\D/g, "").slice(-10) === digits
    );
    if (match) return match.name || match.role;
  }
  // The webhook writes 'unknown' when a sender matches no group member, no
  // workshop stakeholder, and neither number on the job card — someone added
  // to the group by hand, or a staff member whose number was never recorded.
  // Show the number so a human can recognise them; "unknown" alone tells the
  // advisor nothing and reads like a bug.
  if (role === "unknown" || !role) {
    return digits ? `Unknown · ${digits}` : "Unknown participant";
  }
  if (roleShort) return roleShort(role) || role || "Unknown participant";
  return role || "Unknown participant";
}

export function msgDateLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const diff = Math.floor((today.setHours(0, 0, 0, 0) - d.setHours(0, 0, 0, 0)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export function msgDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export function DateSeparator({ label }) {
  return (
    <div className="flex items-center justify-center py-1">
      <span className="rounded-full border border-slate-200 bg-white px-3 py-0.5 text-[11px] font-medium text-slate-500 shadow-sm">
        {label}
      </span>
    </div>
  );
}

// A template row stores the template's own name — "auto_repair_order",
// "waa_invite_2" — because that is what was sent to Chat360. Printed raw it
// tells an advisor nothing, so it is turned back into the thing the customer
// actually received.
//
// Derived rather than looked up: every tenant names its own templates, so an
// exhaustive map would go stale the first time a brand is onboarded. The map
// below only covers names whose slug does not read as English on its own.
const TEMPLATE_LABELS = {
  initial_msz1: "Vehicle received",
  initial_msz: "Vehicle received",
  ro_estimate: "Estimate",
  preinvoice: "Pre-invoice",
  pre_invoice: "Pre-invoice",
  nps: "Feedback survey",
  feedback_cia: "Feedback request",
  next_service_due: "Next service reminder",
  invite: "Group invite",
};

// A template row's message_text is either the real rendered copy (the brand
// configured a Message preview in Super Admin, so this is what the customer
// actually received) or, when none was configured, just the bare Chat360
// template code ("auto_repair_order") the same way it always was. Slugs are
// snake_case with no spaces; real rendered text always has them.
export function templateLooksRendered(text) {
  return /\s/.test(String(text || ""));
}

export function templateLabel(name) {
  const raw = String(name || "").trim();
  if (!raw) return "Automated message";
  // Strip the vendor prefix every template in this account carries.
  const slug = raw.replace(/^(auto|waa|wa)[_-]/i, "").toLowerCase();
  if (TEMPLATE_LABELS[slug]) return TEMPLATE_LABELS[slug];
  const words = slug.replace(/[_-]+/g, " ").trim();
  if (!words) return raw;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|heic)$/i;
const VIDEO_EXT = /\.(mp4|mov|webm|m4v|avi|3gp)$/i;

function urlExtension(url) {
  try {
    const name = decodeURIComponent(new URL(url).pathname.split("/").pop() || "");
    return name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  } catch {
    return "";
  }
}

// What a media row actually is. The worker used to log every work-progress or
// mixed send as "document", so a photo drew as a PDF card; the file's own
// extension is the more reliable signal, and it repairs rows already stored.
// Only "document" (and a missing type) is second-guessed — a row that already
// says image/video/audio is believed.
export function effectiveMediaType(type, url) {
  if (type && type !== "document") return type;
  const ext = urlExtension(url);
  if (IMAGE_EXT.test(`.${ext}`)) return "image";
  if (VIDEO_EXT.test(`.${ext}`)) return "video";
  return type || "";
}

// "PDF", "DOCX" … from the file's extension, so the card does not call a
// spreadsheet a PDF.
export function documentKindLabel(url) {
  const ext = urlExtension(url);
  return ext ? ext.toUpperCase() : "File";
}

// Journey stages the advisor triggers by hand (uploading a file, picking a
// delivery option). This only covers rows logged before uploads were recorded
// as the advisor's own message (source "dashboard"), which now carry that
// themselves. Inventory (media), Estimate and Job Card are left out on purpose:
// the booking's copies of them are also sent automatically (Let's get started,
// estimate approved) under those same stage names, and those really are
// automated. Their send goes
// through the same worker as the automatic ones, so it is logged as
// "automation" — but nobody would call a photo an advisor just attached
// automated. The desk shows these as the advisor's own message, and keeps the
// Automated label for what really fires on its own (welcome, reminders,
// feedback, auto-replies).
export const MANUAL_UPLOAD_STAGES = new Set([
  "work_progress",
  "delivery_media",
  "delivery",
  "parts_approval",
  "pre_invoice",
  "invoice",
]);

export function isAutomatedMessage(msg) {
  const automated = msg?.source === "automation" || msg?.sender_role === "system";
  return automated && !MANUAL_UPLOAD_STAGES.has(msg?.stage);
}
